import { Circle } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAvailability } from '@/hooks/useAvailability';
import { cn } from '@/utils';

import { UserAvatar, type UserAvatarProps } from './UserAvatar';

export interface AvatarWithStatusProps extends UserAvatarProps {
  /** Whether clicking the avatar opens the availability popover. Defaults to true. */
  interactive?: boolean;
}

/**
 * AvatarWithStatus — wraps UserAvatar with an online/offline status dot.
 *
 * When `interactive` is true (default), clicking opens a popover to toggle
 * between "Available" and "Not Available" using the useAvailability hook.
 */
export const AvatarWithStatus: React.FC<AvatarWithStatusProps> = ({
  interactive = true,
  size = 'md',
  className,
  ...avatarProps
}) => {
  const { available, isLoading, isPending, setAvailable } = useAvailability();
  const [open, setOpen] = useState(false);

  const avatar = (
    <UserAvatar
      size={size}
      className={className}
      available={isLoading ? undefined : available}
      {...avatarProps}
    />
  );

  if (!interactive) {
    return avatar;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full" disabled={isPending}>
          {avatar}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1" side="top" align="start">
        <Button
          variant="ghost"
          className={cn(
            'flex w-full justify-start gap-2 px-2 py-1.5 h-auto',
            available && 'bg-accent',
          )}
          onClick={() => {
            setAvailable(true);
            setOpen(false);
          }}
          disabled={isPending}
        >
          <Circle className="h-2.5 w-2.5 fill-success text-success shrink-0" />
          <span className="text-success">Available</span>
        </Button>
        <Button
          variant="ghost"
          className={cn(
            'flex w-full justify-start gap-2 px-2 py-1.5 h-auto',
            !available && 'bg-accent',
          )}
          onClick={() => {
            setAvailable(false);
            setOpen(false);
          }}
          disabled={isPending}
        >
          <Circle className="h-2.5 w-2.5 fill-muted-foreground text-muted-foreground shrink-0" />
          Not Available
        </Button>
      </PopoverContent>
    </Popover>
  );
};
